import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import { ref } from "vue";
import { AuthType, FileStat, createClient } from "webdav";
import * as graphql from "../graphql";
import { ServerTimeDocument } from "../graphql";
import apolloClient from "../graphql/client";
import { ExtendedWebDAVClient } from "../lib/zip_files/ExtendedWebDAVClient";
import TimeTickWorker from "../worker/timeTickWorker?worker";

export const AUTO_SCROLL_DISABLED_LOCAL_STORAGE_KEY = "autoScrollDisabled";

interface FileManager {
  client: ExtendedWebDAVClient | null;
}

export const useClientStore = defineStore("client", () => {
  const clientTime = ref(Date.now());
  const serverTime = ref<number>();
  const me = ref();
  const isAuthenticated = ref<boolean>(false);
  const authToken = ref<string | null>(null);
  const fileManager = ref<FileManager>({ client: null });
  const serverTimeOffset = ref<number>();
  const isReading = ref<boolean>(true);
  const fileName = ref("");
  const errorReading = ref<boolean>(false);
  const isNotFullScreen = ref<boolean>(true);
  const fileIsOpened = ref<boolean>(false);
  const isEditorOpened = ref<boolean>(false);
  const interactivityLockVisibility = ref<boolean>(true);

  const fileRefresh = ref<boolean>(false);
  const increaseFileSize = ref<boolean>(false);

  function tick() {
    clientTime.value = Date.now();
    serverTime.value = serverTimeOffset.value ? Date.now() + serverTimeOffset.value : 0;
  }
  function setAuthStatus(isAuth: boolean) {
    isAuthenticated.value = isAuth;
  }
  function setAuthToken(authTokenState: string | null) {
    authToken.value = authTokenState;
    if (fileManager.value.client) {
      fileManager.value.client.setHeaders({
        Authorization: `Bearer ${authToken.value}`,
      });
    }
  }

  function setFileManagerClient(rsid) {
    const baseUrl = `https://${rsid.region}-${rsid.id}.g-portal.services/${rsid.id}/`;
    const originalClient = createClient(baseUrl, {
      authType: AuthType.None, // we set the token here based on the keycloak token
    });

    fileManager.value.client = new ExtendedWebDAVClient(originalClient, baseUrl);
    setAuthToken(authToken.value);
  }

  function setReading(reading: boolean) {
    isReading.value = reading;
  }

  function setFileName(file: string | undefined) {
    if (file != null) {
      fileName.value = file;
    }
  }
  function setOpenFile(isOpened: boolean) {
    fileIsOpened.value = isOpened;
  }
  function setErrorReading(error: boolean) {
    errorReading.value = error;
  }
  function setFullScreen(fullscreen: boolean) {
    isNotFullScreen.value = fullscreen;
  }
  function setRefresh(value: boolean) {
    fileRefresh.value = value;
  }
  function setFileSize(value: boolean) {
    increaseFileSize.value = value;
    setRefresh(value);
  }
  function setInteractivityLockVisibility(value: boolean) {
    interactivityLockVisibility.value = value;
  }

  const autoScrollDisabled = useLocalStorage(AUTO_SCROLL_DISABLED_LOCAL_STORAGE_KEY, false, {
    shallow: true,
    writeDefaults: false,
  });
  const autoScroll = ref(!autoScrollDisabled.value);
  function setAutoScroll(isAuto: boolean) {
    autoScroll.value = isAuto;
    autoScrollDisabled.value = !isAuto;
  }
  function setIsInEditor(isInEditor: boolean) {
    isEditorOpened.value = isInEditor;
  }
  function downloadFile(item: FileStat) {
    const anchor = document.createElement("a");
    const downloadUri = fileManager.value.client?.getFileDownloadLink(item);
    if (downloadUri) {
      anchor.href = `${downloadUri}?token=${authToken.value}&download=1`;
      anchor.target = "_blank";
      anchor.click();
      anchor.remove();
    } else {
      console.debug("URL not valid");
    }
  }
  async function loadMe(region: graphql.Region) {
    const result = await apolloClient.query({
      query: graphql.MeDocument,
      variables: {
        region,
      },
    });
    me.value = result.data.me;
  }
  async function updateServerTimeOffset() {
    const requestStart = Date.now();
    const result = await apolloClient.query({
      query: ServerTimeDocument,
    });
    const requestDuration = Date.now() - requestStart;
    if (result.data.serverTime) {
      serverTimeOffset.value = result.data.serverTime - requestDuration / 2 - requestStart;
    }
  }
  const showMobileNav = ref(false);
  function setMobileNav(show: boolean) {
    showMobileNav.value = show;
  }
  return {
    clientTime,
    serverTime,
    showMobileNav,
    setMobileNav,
    serverTimeOffset,
    updateServerTimeOffset,
    loadMe,
    me,
    setFileManagerClient,
    setAuthToken,
    authToken,
    isAuthenticated,
    setAuthStatus,
    fileManager,
    tick,
    setReading,
    isReading,
    setErrorReading,
    errorReading,
    setFullScreen,
    isNotFullScreen,
    setFileName,
    fileName,
    setOpenFile,
    fileIsOpened,
    downloadFile,
    setIsInEditor,
    isEditorOpened,
    setAutoScroll,
    autoScroll,
    fileRefresh,
    setRefresh,
    setFileSize,
    increaseFileSize,
    interactivityLockVisibility,
    setInteractivityLockVisibility,
  };
});

const timeTickWorker = new TimeTickWorker();
timeTickWorker.onmessage = () => {
  useClientStore().tick();
};
timeTickWorker.postMessage("start");
